/**
 * Turning a stream of tokens into something a screen reader can follow (checkbox one).
 *
 * The product is a wall of text that arrives asynchronously, and that is exactly the shape a
 * live region handles worst. `narration.delta` events land in fragments — *"The tarp"*,
 * *"aulin gives with a"*, *"sound like a held breath."* — and a polite region pointed at that
 * announces each fragment as it arrives: a stutter of half-words for twenty seconds, with no
 * way to skim back. The accessibility brief says *"announce on sentence or on completion"*, and
 * this module is the
 * "on sentence" half: pure, so the rule can be tested against strings rather than against a
 * screen reader nobody has running in CI.
 *
 * **A sentence is announceable once its terminator has been followed by whitespace.** That is
 * the whole rule, and the trailing-whitespace part is the load-bearing half: `"He turned."` in
 * a still-open stream may be the start of `"He turned.38 rounds left."` — no, more to the
 * point, of `"He turned. The"`, and announcing before the space arrives means announcing a
 * sentence that may still grow a closing quote or an ellipsis. Whitespace after the terminator
 * proves the model moved on.
 *
 * The tail — everything after the last complete sentence — is held back until the caller says
 * the turn is over, which is what `complete` is for. Prose that never gets a terminator (a
 * fragment, a line of dialogue cut short) is therefore announced exactly once, at the end,
 * rather than never.
 */

/**
 * A sentence terminator, plus any closing punctuation that belongs to it, immediately before
 * whitespace. The lookahead rather than a consumed `\s` keeps the boundary at the end of the
 * sentence, so the whitespace stays with the *next* chunk and gets trimmed off it there.
 *
 * `…` is in the set because the DM writes it (the i18n bundle is full of them) and a trailing
 * off is a complete thought. A bare `.` inside `3.5` or `Mr. Smith` is a false boundary and is
 * accepted: the cost is one short announcement, the alternative is an abbreviation lexicon per
 * locale, and the three locales here do not agree on one.
 */
const SENTENCE_END = /[.!?…]["'”’»)\]]*(?=\s)/g;

/**
 * The chunks of `text` that are ready to be spoken, oldest first.
 *
 * Deterministic and prefix-stable: for a growing `text` the result only ever gains entries,
 * which is what lets a caller announce the difference rather than diffing prose.
 *
 * @param complete the stream is over, so the tail is as finished as it will ever be.
 */
export function announceableSentences(text: string, complete: boolean): string[] {
  const chunks: string[] = [];
  let cursor = 0;

  SENTENCE_END.lastIndex = 0;
  for (let match = SENTENCE_END.exec(text); match !== null; match = SENTENCE_END.exec(text)) {
    const end = match.index + match[0].length;
    const chunk = text.slice(cursor, end).trim();
    if (chunk.length > 0) chunks.push(chunk);
    cursor = end;
  }

  if (complete) {
    const tail = text.slice(cursor).trim();
    if (tail.length > 0) chunks.push(tail);
  }

  return chunks;
}

/**
 * Whether `next` continues `previous` or starts something new.
 *
 * The announcer needs this because the turn store is *cleared* on commit — `narration` goes
 * from a finished chapter back to `''` — and an announcer that reset on that would drop the
 * final sentence in the frame before it spoke it. Within one turn narration is append-only, so
 * "is a prefix of" is an exact test for *the same turn, further along*, and everything else is
 * either the commit (empty) or the next turn (different prose).
 */
export function continuesNarration(previous: string, next: string): boolean {
  return next.startsWith(previous);
}
